import React, { useState, useEffect } from 'react';
import { api } from '../config/api';
import { useLocation as useLocationContext } from '../contexts/LocationContext';
import MovieCard from './MovieCard';
import NearbyLocationsList from './NearbyLocationsList';
import './MovieCard.css';

const MovieArea = () => {
  const { getLocationForAPI, selectedCity, userLocation } = useLocationContext();
  const [activeTab, setActiveTab] = useState('now-showing');
  const [nowShowingMovies, setNowShowingMovies] = useState([]);
  const [comingSoonMovies, setComingSoonMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMovies = async () => {
      try {
        setLoading(true);
        setError(null);

        // Resolve the user's location for the Now Showing call.
        // Coming Soon stays global (no location filter).
        const loc = getLocationForAPI();
        const locationParams = loc
          ? { latitude: loc.lat, longitude: loc.lng, limit: 50 }
          : { limit: 50 };

        const [nowShowing, comingSoon] = await Promise.all([
          api.getNowShowingByLocation(locationParams),
          api.getMovies({ status: 'Coming Soon', limit: 50 }),
        ]);

        setNowShowingMovies(Array.isArray(nowShowing) ? nowShowing : []);
        setComingSoonMovies(
          Array.isArray(comingSoon)
            ? comingSoon.filter(m => m.status === 'Coming Soon')
            : []
        );
      } catch (err) {
        console.error('Error fetching movies:', err);
        setError(err.message);
        setNowShowingMovies([]);
        setComingSoonMovies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMovies();
  }, [selectedCity, userLocation, getLocationForAPI]);

  // Re-init Magnific Popup after movies load
  useEffect(() => {
    if (!loading && (nowShowingMovies.length > 0 || comingSoonMovies.length > 0)) {
      const timer = setTimeout(() => {
        if (window.$ && window.$.fn.magnificPopup) {
          window.$('.mc-play.popup-youtube').off('click.magnificPopup');
          window.$('.mc-play.popup-youtube').magnificPopup({
            type: 'iframe',
            mainClass: 'mfp-fade',
            removalDelay: 160,
            preloader: false,
            fixedContentPos: false,
            iframe: {
              patterns: {
                youtube: {
                  index: 'youtube.com/',
                  id: 'v=',
                  src:
                    'https://www.youtube.com/embed/%id%?autoplay=1&rel=0&modestbranding=1&showinfo=0&controls=1&fs=1&iv_load_policy=3&cc_load_policy=0&playsinline=1&origin=' +
                    encodeURIComponent(window.location.origin) +
                    '&enablejsapi=1',
                },
              },
            },
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, nowShowingMovies, comingSoonMovies]);

  const renderMovies = (movies, bookable = true) => {
    if (!movies.length) {
      const emptyMessage = bookable
        ? 'No movies currently showing for the selected location'
        : 'No upcoming movies.';
      return (
        <div className="col-12 text-center py-5">
          <p>{emptyMessage}</p>
          {bookable && <NearbyLocationsList />}
        </div>
      );
    }
    return (
      // mc-scroll  → horizontal scroll on mobile, grid on desktop
      // swap to mc-grid if you always want a grid (no scroll)
      <div className="mc-scroll">
        {movies.map(movie => (
          <div key={movie.id}>
            <MovieCard movie={movie} showBookButton={bookable} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="movie-area">
      <div className="container">
        {/* Tabs */}
        <ul className="nav nav-pills mb-4 th-tab" id="pills-tab" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'now-showing' ? 'active' : ''}`}
              onClick={() => setActiveTab('now-showing')}
              type="button"
            >
              Now Showing
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'coming-soon' ? 'active' : ''}`}
              onClick={() => setActiveTab('coming-soon')}
              type="button"
            >
              Coming Soon
            </button>
          </li>
        </ul>

        {/* Content */}
        <div className="tab-content">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading movies...</span>
              </div>
              <p className="mt-3">Loading movies...</p>
            </div>
          ) : error ? (
            <div className="alert alert-warning text-center">
              <p>Unable to load movies. Please try again later.</p>
            </div>
          ) : (
            <>
              <div className={`tab-pane fade ${activeTab === 'now-showing' ? 'show active' : ''}`}>
                {renderMovies(nowShowingMovies, true)}
              </div>
              <div className={`tab-pane fade ${activeTab === 'coming-soon' ? 'show active' : ''}`}>
                {renderMovies(comingSoonMovies, false)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MovieArea;